import { createSignal, Match, Show, Switch, type VoidComponent } from "solid-js";
import { type InferOutput, object } from "valibot";
import pMinDelay from "p-min-delay";
import { keyFromSelector } from "i18next";
import { useTranslation } from "../../../../../state/i18n";
import SettingSection from "../../components/SettingSection";
import { TextInput } from "../../../../../components/inputs/TextInput.tsx";
import { IconButton, TextButton } from "../../../../../components/button/Button.tsx";
import BillingService, { type CouponDto, type DiscountDto } from "../../../../../services/BillingService";
import { createForm, type FormApi } from "../../../../../components/form/createForm";
import { Form } from "../../../../../components/form/Form";
import { requireText } from "../../../../../utils/validation/base";
import { MAX_CHARACTERS_XS, MINIMUM_ACTION_LOADING_DELAY } from "../../../../../constants";
import { useModalManager } from "../../../../../components/modal/ModalManager";
import GenericFailureModal from "../../../../GenericFailureModal";
import { MoreAppError } from "../../../../../MoreAppError";
import { IconTile } from "../../../../../components/icon/IconTile";
import Pill from "../../../../../components/pill/Pill";
import FormatCurrency from "../../../../../components/formatting/FormatCurrency";
import { formatDate } from "../../../../../components/formatting/FormatDate";
import { seconds } from "../../../../../utils/timeUtil";
import { Translation } from "../../../../../utils/Translation";

const CouponSchema = object({
  coupon: requireText(MAX_CHARACTERS_XS, true),
});

type CouponFormData = InferOutput<typeof CouponSchema>;

type CouponSectionProps = {
  discount: DiscountDto | null;
};

const CouponSection: VoidComponent<CouponSectionProps> = (props) => {
  const { t } = useTranslation();
  const { open } = useModalManager();
  const addCoupon = BillingService.addCoupon();
  const deleteCoupon = BillingService.deleteCoupon();

  const [rejectedCoupon, setRejectedCoupon] = createSignal<string>();
  const [removing, setRemoving] = createSignal(false);

  const form = createForm<CouponFormData>(
    CouponSchema,
    async ({ coupon }) => {
      await pMinDelay(addCoupon.mutateAsync(coupon), MINIMUM_ACTION_LOADING_DELAY)
        .then(() => form.reset())
        .catch((error: unknown) => {
          // The API responds with a 400 for any coupon code it can't apply
          if (error instanceof MoreAppError && error.status === 400) {
            setRejectedCoupon(coupon);
          } else {
            open({ content: () => <GenericFailureModal error={error} /> });
          }
        });
    },
    { coupon: "" },
  );

  const isRejected = (): boolean => rejectedCoupon() === form.getValue("coupon")?.trim();

  const removeCoupon = async (): Promise<void> => {
    setRemoving(true);
    await pMinDelay(deleteCoupon.mutateAsync(), MINIMUM_ACTION_LOADING_DELAY)
      .catch((error: unknown) => open({ content: () => <GenericFailureModal error={error} /> }))
      .finally(() => setRemoving(false));
  };

  return (
    <SettingSection
      ariaIdPrefix="coupon"
      title={t(($) => $.SETTINGS_BILLING_COUPON_TITLE)}
      description={t(($) => $.SETTINGS_BILLING_COUPON_DESCRIPTION)}
    >
      <Show when={props.discount} fallback={<CouponInput form={form} rejected={isRejected()} />}>
        {(discount) => <AppliedCoupon discount={discount()} removing={removing()} onRemove={removeCoupon} />}
      </Show>
    </SettingSection>
  );
};

type CouponInputProps = {
  form: FormApi<CouponFormData>;
  rejected: boolean;
};

const CouponInput: VoidComponent<CouponInputProps> = (props) => {
  const { t } = useTranslation();

  return (
    <Form form={props.form} class="flex w-full items-start gap-1">
      {({ value, updateValue, error }) => (
        <>
          <div class="grow">
            <TextInput
              value={value("coupon")}
              disabled={props.form.isSubmitting()}
              errorMessage={
                error("coupon") ?? (props.rejected ? t(($) => $.SETTINGS_BILLING_COUPON_INVALID) : undefined)
              }
              onInput={(e) => updateValue("coupon", e.currentTarget.value)}
              onBlur={(e) => updateValue("coupon", e.currentTarget.value)}
              label={t(($) => $.SETTINGS_BILLING_COUPON_INPUT_LABEL)}
            />
          </div>
          <TextButton
            class="mt-6"
            type="submit"
            text={t(($) => $.SETTINGS_BILLING_COUPON_APPLY_BUTTON)}
            disabled={props.form.submitDisabled() || props.rejected}
            loading={props.form.isSubmitting()}
          />
        </>
      )}
    </Form>
  );
};

type AppliedCouponProps = {
  discount: DiscountDto;
  removing: boolean;
  onRemove: () => void;
};

const AppliedCoupon: VoidComponent<AppliedCouponProps> = (props) => {
  const { t } = useTranslation();

  return (
    <div class="flex grow items-center gap-3">
      <IconTile icon="tag" size="md" border />
      <div class="flex flex-col">
        <span class="text-medium-regular text-primary">
          <Translation
            key={keyFromSelector(($) => $.SETTINGS_BILLING_COUPON_DISCOUNT_TITLE)}
            components={{ discount: () => <CouponAmount coupon={props.discount.coupon} /> }}
          />
        </span>
        <Show when={props.discount.end}>
          {(end) => (
            <span class="text-base-regular text-secondary">
              {t(($) => $.SETTINGS_BILLING_COUPON_EXPIRES, { date: formatDate(new Date(seconds(end())), true) })}
            </span>
          )}
        </Show>
      </div>
      <div class="ml-auto flex items-center gap-2">
        <Pill variant="neutral" label={props.discount.couponCode ?? props.discount.coupon.id} class="mr-8" />
        <IconButton
          variant="tertiary"
          icon="trash"
          aria-label={t(($) => $.SETTINGS_BILLING_COUPON_REMOVE_ARIA_LABEL)}
          loading={props.removing}
          onClick={props.onRemove}
        />
      </div>
    </div>
  );
};

const CouponAmount: VoidComponent<{ coupon: CouponDto }> = (props) => (
  <Switch>
    <Match when={props.coupon.percentOff}>{(percentOff) => `${percentOff()}%`}</Match>
    <Match when={props.coupon.amountOff}>
      {(amountOff) => <FormatCurrency value={amountOff() / 100} currency={props.coupon.currency ?? ""} />}
    </Match>
  </Switch>
);

export default CouponSection;
